import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apoioService } from "@/features/fiscal/services/fiscalService";

interface Props {
  value?:       number | null;   // id da natureza da receita
  onChange:     (id: number | null) => void;
  placeholder?: string;
  disabled?:    boolean;
}

/** Natureza da Receita (EFD-Contribuições 4.3.x) com busca no servidor por código/descrição. */
export function NaturezaReceitaSearchCombobox({
  value,
  onChange,
  placeholder = "Buscar natureza da receita...",
  disabled,
}: Props) {
  const [open, setOpen]         = useState(false);
  const [search, setSearch]     = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["natureza-receita-search", debounced],
    queryFn:  () => apoioService.naturezasReceita(debounced || undefined),
    enabled:  open,
    staleTime: 60_000,
  });
  const results = data ?? [];

  const { data: selecionado } = useQuery({
    queryKey: ["natureza-receita-selected", value],
    queryFn:  () => apoioService.naturezasReceita(undefined, value ?? undefined),
    enabled:  value != null,
    staleTime: 10 * 60_000,
    select:   (list) => list[0],
  });

  const label = selecionado
    ? `${selecionado.codigo ?? ""} — ${selecionado.descricao ?? ""}`
    : value != null ? `#${value}` : placeholder;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-8 text-sm"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate text-left", value == null && "text-muted-foreground")}>{label}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[520px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Digite código ou descrição..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!isLoading && <CommandEmpty>Nenhuma natureza encontrada.</CommandEmpty>}
              <CommandGroup>
                {results.map(n => (
                  <CommandItem
                    key={n.id}
                    value={String(n.id)}
                    onSelect={() => { onChange(n.id); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === n.id ? "opacity-100" : "opacity-0")} />
                    <div className="flex flex-col min-w-0">
                      <span className="font-mono text-sm">{n.codigo}</span>
                      <span className="text-xs text-muted-foreground truncate">{n.descricao}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value != null && !disabled && (
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Limpar" onClick={() => onChange(null)}>
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
